import { useEffect, useRef, useState } from 'react';
import loaderWebm from '@lib/assets/images/loader-420.webm';
import loaderMp4 from '@lib/assets/images/loader-420.mp4';
import Logo from '@lib/assets/images/logo.svg?react';
import styles from './LogoLoader.module.scss';

const sources = [
  { src: loaderWebm, type: 'video/webm', key: 'webm' },
  { src: loaderMp4, type: 'video/mp4', key: 'mp4' },
];

// How long the static logo stays on screen when the video can't play
const LOGO_TIMEOUT_MS = 2000;
// If the video hasn't started by then, give up on it. Covers autoplay being
// blocked, iOS 26 where play() may never settle, and failed sources (errors
// fire on <source>, not on <video>).
const PLAY_START_TIMEOUT_MS = 800;
// Safety margin in case `ended` never fires
const END_MARGIN_MS = 1000;
const FALLBACK_VIDEO_DURATION_S = 3;

export default function LogoLoader({ onFinish }: { onFinish: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showLogo, setShowLogo] = useState(false);

  useEffect(() => {
    if (showLogo) {
      const timeoutId = window.setTimeout(onFinish, LOGO_TIMEOUT_MS);
      return () => window.clearTimeout(timeoutId);
    }

    const video = videoRef.current;
    if (!video) {
      setShowLogo(true);
      return undefined;
    }

    let endTimeoutId: number | undefined;
    const startTimeoutId = window.setTimeout(
      () => setShowLogo(true),
      PLAY_START_TIMEOUT_MS,
    );

    const handlePlaying = () => {
      window.clearTimeout(startTimeoutId);
      window.clearTimeout(endTimeoutId);
      const duration = video.duration || FALLBACK_VIDEO_DURATION_S;
      endTimeoutId = window.setTimeout(
        onFinish,
        duration * 1000 + END_MARGIN_MS,
      );
    };

    video.addEventListener('playing', handlePlaying);
    video.play()?.catch(() => setShowLogo(true));

    return () => {
      video.removeEventListener('playing', handlePlaying);
      window.clearTimeout(startTimeoutId);
      window.clearTimeout(endTimeoutId);
    };
  }, [showLogo, onFinish]);

  return (
    <div className={styles.loaderWrap}>
      {showLogo ? (
        <Logo className={styles.loaderLogo} />
      ) : (
        <video
          className={styles.loaderVideo}
          controls={false}
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          muted
          onEnded={onFinish}
          playsInline
          preload="auto"
          ref={videoRef}
        >
          {sources.map((source) => (
            <source key={source.key} src={source.src} type={source.type} />
          ))}
        </video>
      )}
    </div>
  );
}
